<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTariffsQuery } from '@/entities/tariff'
import CTab from '@/shared/ui/CTab.vue'

const { t } = useI18n()
const { data: tariffsData } = useTariffsQuery()

// Active tab state
const activeTab = ref('1')

// Tabs configuration
const tabsConfig = computed(() =>
  [1, 3, 12, 24].map(p => ({
    label: `${p} ${t('month_plural', p)}`,
    value: String(p),
  })),
)

// Essential features list
const essentialFeatures = computed(() => [
  t('tariff_feature_dashboard'),
  t('tariff_feature_calculator'),
  t('tariff_feature_supply_management'),
  t('tariff_feature_reviews'),
  t('tariff_feature_ai_chat'),
])

// Premium-only features
const premiumOnlyFeatures = computed(() => [
  t('tariff_feature_repricer_full'),
  t('tariff_feature_autobidder'),
  t('tariff_feature_multi_store'),
  t('tariff_feature_priority_support'),
])

// Base monthly prices
const MONTHLY_PRICES = {
  essential: 3990,
  premium: 4990,
}

// Discount rates per period
const DISCOUNTS: Record<string, { rate: number, label: string | null }> = {
  1: { rate: 0, label: null },
  3: { rate: 0.05, label: '5%' },
  12: { rate: 0.15, label: '15%' },
  24: { rate: 0.25, label: '25%' },
}

// Auto-calculate pricing for any period
function calcPrice(monthly: number, months: number, discountRate: number) {
  const total = monthly * months
  const discounted = Math.round(total * (1 - discountRate))
  return discounted
}

const formatPrice = (n: number) => n.toLocaleString('ru-RU')

// Build pricing from API data with hardcoded fallback
function buildPricing(type: 'essential' | 'premium', months: number) {
  const tariff = tariffsData.value?.find(t => t.type === type)
  const option = tariff?.options.find(o => o.period === months)

  if (option) {
    const original = Math.round(Number(option.price))
    const price = Math.round(Number(option.discounted_price ?? option.price))
    const hasDiscount = price < original

    return {
      price,
      suffix: months === 1 ? '₽/мес.' : '₽',
      discount: hasDiscount
        ? `${Math.round((1 - price / original) * 100)}%`
        : null,
      originalPrice: hasDiscount ? original : null,
    }
  }

  // Fallback to hardcoded
  const { rate, label } = DISCOUNTS[String(months)]
  const monthly = MONTHLY_PRICES[type]

  return {
    price: months === 1 ? monthly : calcPrice(monthly, months, rate),
    suffix: months === 1 ? '₽/мес.' : '₽',
    discount: label,
    originalPrice: label ? monthly * months : null,
  }
}

// Current pricing based on selected tab
const currentPricing = computed(() => {
  const months = Number(activeTab.value)

  return {
    essential: buildPricing('essential', months),
    premium: buildPricing('premium', months),
  }
})
</script>

<template>
  <section class="bg-white py-8 md:py-20">
    <div class="container mx-auto flex flex-col gap-4 lg:gap-10">
      <h2
        data-aos="fade-up"
        data-aos-duration="800"
        data-aos-delay="100"
        class="font-semibold text-[32px] sm:text-4xl md:text-5xl text-[#172026] text-center leading-10 md:leading-[72px]"
      >
        {{ $t("landing_pricing_title") }}
      </h2>

      <!-- Period Tabs -->
      <div
        data-aos="fade-up"
        data-aos-duration="800"
        data-aos-delay="150"
        class="flex justify-center"
      >
        <CTab v-model="activeTab" :tabs="tabsConfig" class="landing-tabs" />
      </div>

      <div
        data-aos="fade-up"
        data-aos-duration="1000"
        data-aos-delay="200"
        class="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto"
      >
        <!-- Essential Plan -->
        <div
          class="bg-[#F4F8FA] rounded-2xl p-6 lg:p-8 flex flex-col min-h-[500px]"
        >
          <h3 class="font-semibold text-2xl text-[#172026] mb-4">
            {{ $t("tariff_essential") }}
          </h3>

          <!-- Price moved up and prominent -->
          <div class="mb-6">
            <!-- Discount badge and original price -->
            <div
              v-if="currentPricing.essential.discount"
              class="flex items-center justify-center gap-2 mb-2"
            >
              <span class="text-lg text-[#36485C] line-through">
                {{ formatPrice(currentPricing.essential.originalPrice!) }} ₽
              </span>
              <span
                class="bg-[#67C23A] text-white text-xs font-medium px-2 py-1 rounded"
              >
                -{{ currentPricing.essential.discount }}
              </span>
            </div>
            <div class="flex items-baseline justify-center">
              <span class="text-4xl font-bold text-[#172026]">
                {{ formatPrice(currentPricing.essential.price) }}
              </span>
              <span class="text-lg text-[#36485C] ml-1">{{
                currentPricing.essential.suffix
              }}</span>
            </div>
          </div>

          <div class="mb-8 flex-grow">
            <ul class="space-y-3">
              <li
                v-for="feature in essentialFeatures"
                :key="feature"
                class="flex items-start text-sm text-[#36485C]"
              >
                <svg
                  class="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span>{{ feature }}</span>
              </li>
            </ul>
          </div>

          <router-link
            to="/register"
            class="border-2 border-[#4F46E5] text-[#4F46E5] text-center py-3 px-6 rounded-lg font-semibold hover:bg-[#4F46E5] hover:text-white transition-colors duration-200"
          >
            Начать сейчас
          </router-link>
        </div>

        <!-- Premium Plan -->
        <div
          class="relative bg-gradient-to-br from-[#F4F8FA] to-[#F0F4FF] rounded-2xl p-6 lg:p-8 flex flex-col border-2 border-blue-500 shadow-xl transform scale-105 min-h-[500px]"
        >
          <!-- Popular Badge -->
          <div class="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span
              class="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold"
            >
              Популярный
            </span>
          </div>

          <h3 class="font-semibold text-2xl text-[#172026] mb-4 mt-2">
            {{ $t("tariff_premium") }}
          </h3>

          <!-- Price moved up and prominent -->
          <div class="mb-6">
            <!-- Discount badge and original price -->
            <div
              v-if="currentPricing.premium.discount"
              class="flex items-center justify-center gap-2 mb-2"
            >
              <span class="text-lg text-[#36485C] line-through">
                {{ formatPrice(currentPricing.premium.originalPrice!) }} ₽
              </span>
              <span
                class="bg-[#67C23A] text-white text-xs font-medium px-2 py-1 rounded"
              >
                -{{ currentPricing.premium.discount }}
              </span>
            </div>
            <div class="flex items-baseline justify-center">
              <span class="text-4xl font-bold text-[#172026]">
                {{ formatPrice(currentPricing.premium.price) }}
              </span>
              <span class="text-lg text-[#36485C] ml-1">{{
                currentPricing.premium.suffix
              }}</span>
            </div>
          </div>

          <div class="mb-8 flex-grow">
            <!-- Premium features with "All from Essential, plus:" header -->
            <p class="text-sm text-[#36485C] mb-4 font-medium">
              Все из Essential, плюс:
            </p>
            <ul class="space-y-3">
              <li
                v-for="feature in premiumOnlyFeatures"
                :key="feature"
                class="flex items-start text-sm text-[#36485C]"
              >
                <svg
                  class="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span>{{ feature }}</span>
              </li>
            </ul>
          </div>

          <router-link
            to="/register"
            class="bg-[#4F46E5] text-white text-center py-4 px-8 rounded-lg font-semibold text-lg hover:bg-[#4338CA] transition-colors duration-200"
          >
            Начать сейчас
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
:deep(.landing-tabs .el-tabs__header) {
  background: #f3f4f6 !important;
}
</style>
